/**
 * Utility interface for managing single-debt, single-collateral Credit Accounts
 */
export interface CdpSingle<T> {
  ltv: bigint;
  liquidationPrice: bigint;
  liquidationPriceDelta: bigint;
  adjustmentMax: bigint;
  account: {
    address: string;
  };
  collateral: {
    asset: T;
    amount: bigint;
    value: bigint;
    valueAdjusted: bigint;
    price: bigint;
    min: bigint;
    minAdjust: bigint;
  };
  debt: {
    asset: T;
    amount: bigint;
    value: bigint;
    rate: bigint;
    price: bigint;
    max: bigint;
    maxAdjust: bigint;
  };
}

export namespace CdpSingle {
  export const fromInput = <T,>(
    deposit: {
      amount: bigint;
      price: bigint;
      asset: T;
      ratio: bigint;
    },
    borrow: {
      amount: bigint;
      price: bigint;
      rate: bigint;
      asset: T;
    },
    config: { adjustmentMax: bigint },
    next?: { account: string }
  ): CdpSingle<T> => {
    const collateralValue = (deposit.amount * deposit.price) / 10n ** 12n;
    const debtValue = (borrow.amount * borrow.price) / 10n ** 12n;

    return compute({
      account: { address: next?.account || "" },
      collateral: {
        asset: deposit.asset,
        amount: deposit.amount,
        value: collateralValue,
        valueAdjusted: (collateralValue * deposit.ratio) / 10n ** 12n,
        price: deposit.price,
        min: 0n,
        minAdjust: 0n,
      },
      debt: {
        asset: borrow.asset,
        amount: borrow.amount,
        value: debtValue,
        rate: borrow.rate,
        price: borrow.price,
        max: 0n,
        maxAdjust: 0n,
      },
      liquidationPrice: 0n,
      liquidationPriceDelta: 0n,
      ltv: 0n,
      adjustmentMax: config.adjustmentMax,
    });
  };

  export const fromGraph = <T,>(
    v: {
      account: {
        address: string;
        label: string;
      };
      collaterals: ReadonlyArray<{
        collateral:
          | {
              __typename: "Balance";
              asset: T;
              amount: bigint;
            }
          | {
              __typename: "%other";
            };
        valueAdjusted: bigint;
        valueFull: bigint;
      }>;
      debts: ReadonlyArray<{
        debt: {
          borrower: {
            asset: T;
            vault: {
              status: {
                debtRate: bigint;
              };
            };
          };
          current: bigint;
        };
        value: bigint;
      }>;
      ltv: bigint;
    },
    config: { adjustmentThreshold: bigint }
  ): CdpSingle<T> => {
    const [collateralAssetStr, debtAssetStr] = v.account.label
      .toString()
      .split("/");

    const collateral = v.collaterals[0] || {
      valueAdjusted: 0n,
      valueFull: 0n,
      collateral: {
        __typename: "Balance",
        asset: {
          asset: collateralAssetStr,
          chain: collateralAssetStr.split("-")[0],
          metadata: {
            symbol: collateralAssetStr.split("-")[1],
          },
        },
        amount: 0n,
      },
    };

    if (collateral.collateral.__typename !== "Balance")
      throw new Error(
        `Unknown collateral type ${collateral.collateral.__typename}`
      );

    const debt = v.debts[0] || {
      value: 0n,
      debt: {
        current: 0n,
        borrower: {
          asset: {
            asset: debtAssetStr,
            chain: debtAssetStr.split("-")[0],
            metadata: {
              symbol: debtAssetStr.split("-")[1],
            },
          },
          vault: { status: { debtRate: 0n } },
        },
      },
    };

    return compute({
      ltv: v.ltv,
      account: v.account,
      adjustmentMax: config.adjustmentThreshold,
      collateral: {
        asset: collateral.collateral.asset,
        amount: collateral.collateral.amount,
        value: collateral.valueFull,
        valueAdjusted: collateral.valueAdjusted,
        price: 0n,
        min: 0n,
        minAdjust: 0n,
      },
      debt: {
        asset: debt.debt.borrower.asset,
        amount: debt.debt.current,
        value: debt.value,
        rate: debt.debt.borrower.vault.status.debtRate,
        price: 0n,
        max: 0n,
        maxAdjust: 0n,
      },
      liquidationPrice: 0n,
      liquidationPriceDelta: 0n,
    });
  };

  const compute = <T,>(p: CdpSingle<T>): CdpSingle<T> => {
    const collateralPrice =
      p.collateral.price ||
      (p.collateral.amount
        ? (p.collateral.value * 10n ** 12n) / p.collateral.amount
        : 0n);

    const debtPrice =
      p.debt.price ||
      (p.debt.amount ? (p.debt.value * 10n ** 12n) / p.debt.amount : 0n);

    const collateralDebtPrice =
      debtPrice && (collateralPrice * 10n ** 12n) / debtPrice;

    const liquidationPrice =
      p.collateral.amount && p.collateral.valueAdjusted
        ? (p.debt.amount * p.collateral.value * 10n ** 12n) /
          (p.collateral.amount * p.collateral.valueAdjusted)
        : 0n;

    const liquidationPriceDelta = collateralDebtPrice
      ? ((liquidationPrice - collateralDebtPrice) * 10n ** 12n) /
        collateralDebtPrice
      : 0n;

    const minCollateral = p.collateral.valueAdjusted
      ? (debtPrice * p.debt.amount * p.collateral.amount) /
        (p.collateral.valueAdjusted * 10n ** 12n)
      : 0n;

    const minCollateralAdjust = p.collateral.valueAdjusted
      ? (minCollateral * 10n ** 12n) / p.adjustmentMax
      : 0n;

    const debtMax = debtPrice
      ? (p.collateral.valueAdjusted * 10n ** 12n) / debtPrice
      : 0n;

    const debtMaxAdjust = (debtMax * p.adjustmentMax) / 10n ** 12n;

    const ltv = p.collateral.valueAdjusted
      ? (p.debt.value * 10n ** 12n) / p.collateral.valueAdjusted
      : 0n;
    return {
      ...p,
      collateral: {
        ...p.collateral,
        price: collateralPrice,
        min: minCollateral,
        minAdjust: minCollateralAdjust,
      },
      debt: {
        ...p.debt,
        price: debtPrice,
        max: debtMax,
        maxAdjust: debtMaxAdjust,
      },
      liquidationPrice,
      liquidationPriceDelta,
      ltv,
    };
  };

  export const depositAndBorrow = <T,>(
    p: CdpSingle<T>,
    depositAmount: bigint,
    borrowAmount: bigint
  ): CdpSingle<T> => {
    const newCollateralAmount = p.collateral.amount + depositAmount;
    const newDebtAmount = p.debt.amount + borrowAmount;

    const newCollateralValueAdjusted = p.collateral.amount
      ? (p.collateral.valueAdjusted * newCollateralAmount) / p.collateral.amount
      : 0n;

    const newCollateralValueFull = p.collateral.amount
      ? (p.collateral.value * newCollateralAmount) / p.collateral.amount
      : 0n;

    const newDebtValue = p.debt.amount
      ? (p.debt.value * newDebtAmount) / p.debt.amount
      : 0n;

    // Update the values that are returned by the api and then recompute the derived values
    return compute({
      ...p,
      collateral: {
        ...p.collateral,
        amount: newCollateralAmount,
        value: newCollateralValueFull,
        valueAdjusted: newCollateralValueAdjusted,
      },
      debt: {
        ...p.debt,
        amount: newDebtAmount,
        value: newDebtValue,
      },
    });
  };

  export const repayAndWithdraw = <T,>(
    p: CdpSingle<T>,
    repayAmount: bigint,
    withdrawAmount: bigint
  ): CdpSingle<T> => depositAndBorrow(p, -withdrawAmount, -repayAmount);

  export const swapCollateral = <T,>(
    p: CdpSingle<T>,
    newAmount: bigint,
    oraclePrice: bigint
  ) =>
    compute({
      ...p,
      collateral: {
        ...p.collateral,
        price: oraclePrice,
        amount: newAmount,
        value: (newAmount * oraclePrice) / 10n ** 12n,
        valueAdjusted:
          (newAmount * oraclePrice * p.collateral.valueAdjusted) /
          (p.collateral.value * 10n ** 12n),
      },
    });
}
